/**
 * Browser capability detection and MediaRecorder configuration.
 */

export interface RecorderConfig {
  mimeType: string;
  fileExtension: string;
  audioBitsPerSecond: number;
}

/**
 * Detect the best available audio recording format.
 * Safari 18.4+ supports WebM/Opus. Older Safari falls back to MP4/AAC.
 */
export function detectRecorderConfig(): RecorderConfig | null {
  if (typeof MediaRecorder === "undefined") return null;

  // Prefer WebM/Opus (universal since Safari 18.4)
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return { mimeType: "audio/webm;codecs=opus", fileExtension: "webm", audioBitsPerSecond: 64000 };
  }
  // Fallback: MP4/AAC (older Safari)
  if (MediaRecorder.isTypeSupported("audio/mp4")) {
    return { mimeType: "audio/mp4", fileExtension: "m4a", audioBitsPerSecond: 64000 };
  }
  // Fallback: plain WebM
  if (MediaRecorder.isTypeSupported("audio/webm")) {
    return { mimeType: "audio/webm", fileExtension: "webm", audioBitsPerSecond: 64000 };
  }

  return null;
}

export function isRecordingSupported(): boolean {
  return typeof navigator !== "undefined"
    && typeof navigator.mediaDevices !== "undefined"
    && typeof MediaRecorder !== "undefined"
    && detectRecorderConfig() !== null;
}
